import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingRegion } from '../contexts/PricingRegionContext';
import { useCart } from '../contexts/CartContext';
import { SECTION_PATHS } from '../config/publicNavigation';
import { smoothScrollToSection } from '../utils/navigation';
import { getIndustryCommonCopy } from '../data/industries';
import { getCurrencySelectorLabel } from '../data/regionalPricing';
import { Globe, Menu, X, ShoppingCart } from 'lucide-react';

// Premium W Logo Component with glass/glow effect
const WebouseLogo = ({ size = 40 }) => (
  <div 
    className="relative flex items-center justify-center"
    style={{
      width: size,
      height: size,
      borderRadius: '14px',
      background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.9) 0%, rgba(0, 200, 165, 0.95) 100%)',
      boxShadow: '0 0 20px rgba(0, 255, 209, 0.4), 0 0 40px rgba(0, 255, 209, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.3)',
      backdropFilter: 'blur(8px)',
    }}
  >
    <svg width={size * 0.65} height={size * 0.65} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path 
        d="M3 6L7 20L10.5 12L13 20L16.5 12L20 20L24 6" 
        stroke="#000000" 
        strokeWidth="2"
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  </div>
);

export const HEADER_NAV_ITEMS = Object.freeze([
  { key: 'about', path: '/about' },
  { key: 'services', path: '/services' },
  { key: 'work', path: '/work' },
  { key: 'resources', path: '/resources' },
  { key: 'pricing', path: '/pricing' },
  { key: 'industries', path: '/industries' },
]);

const Header = () => {
  const { t, currentLang, changeLang, languages, isRTL } = useLanguage();
  const {
    pricingRegion,
    regions,
    isPricingReady,
    changePricingRegion,
    trackPricingEvent,
  } = usePricingRegion();
  const { hasItems, setIsCartOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const industryCopy = getIndustryCommonCopy(currentLang);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (!smoothScrollToSection(id)) {
      navigate(SECTION_PATHS[id] || '/');
    }
    setIsMobileMenuOpen(false);
  };

  const navigateToRoute = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const navItems = HEADER_NAV_ITEMS.map((item) =>
    item.key === 'industries' ? { ...item, label: industryCopy.navLabel } : item
  );
  const desktopNavItems = navItems;
  const currencySelectorLabel = getCurrencySelectorLabel(pricingRegion);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-400 ease-in-out ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-md border-b border-white/10'
          : 'bg-transparent'
      }`}
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className="flex h-20 items-center gap-5 2xl:gap-8">
          {/* Logo */}
          <div
            className="flex w-auto shrink-0 items-center gap-3 cursor-pointer sm:w-[210px]"
            onClick={handleLogoClick}
          >
            <WebouseLogo size={40} />
            <span className="text-white font-semibold text-lg hidden sm:block tracking-tight">
              Webouse
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-3 2xl:flex">
            {desktopNavItems.map((item) => (
              <button
                key={item.key}
                onClick={() =>
                  item.path ? navigateToRoute(item.path) : scrollToSection(item.id)
                }
              className="whitespace-nowrap text-[#4D4D4D] hover:text-white text-sm font-normal transition-colors duration-300"
              >
                {item.label || t(`nav.${item.key}`)}
              </button>
            ))}
          </nav>

          {/* Right Side */}
          <div className="ml-auto flex shrink-0 items-center justify-end gap-3 2xl:gap-4">
            <button
              type="button"
              onClick={() => scrollToSection('contact')}
              className="hidden 2xl:inline-flex items-center justify-center rounded-xl border border-[#00FFD1]/35 bg-[#00FFD1]/10 px-4 py-2.5 text-sm font-semibold text-[#7fffe8] transition-colors hover:border-[#00FFD1]/60 hover:bg-[#00FFD1]/15"
            >
              {t('nav.projectCta')}
            </button>

            {/* Pricing Region Switcher */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsRegionOpen(!isRegionOpen);
                  trackPricingEvent('pricing_selector_opened');
                }}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors p-2"
                aria-label={t('common.pricingCurrency')}
              >
                {isPricingReady ? (
                  <span className="text-sm font-semibold uppercase">
                    {currencySelectorLabel}
                  </span>
                ) : (
                  <span
                    className="h-4 w-14 animate-pulse rounded bg-white/10"
                    aria-label="Detecting pricing region"
                  />
                )}
              </button>
              {isRegionOpen && (
                <div className="absolute top-full mt-2 bg-[#121212] border border-white/10 rounded-none py-2 min-w-[190px] z-50" style={{ [isRTL ? 'left' : 'right']: 0 }}>
                  {regions.filter((region) => region.isActive !== false).map((region) => (
                    <button
                      key={region.id}
                      onClick={() => {
                        changePricingRegion(region.id);
                        setIsRegionOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-white/10 transition-colors ${
                        pricingRegion.id === region.id ? 'text-[#00FFD1]' : 'text-white/70'
                      }`}
                      style={{ textAlign: isRTL ? 'right' : 'left' }}
                    >
                      <span className="font-semibold">{region.currencyCode}</span>
                      <span className="text-white/40 text-xs ms-2">{region.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                aria-label={t('common.languageSelector')}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors p-2"
              >
                <Globe size={20} />
                <span className="text-sm uppercase">{currentLang}</span>
              </button>
              {isLangOpen && (
                <div className="absolute top-full mt-2 bg-[#121212] border border-white/10 rounded-none py-2 min-w-[150px] z-50" style={{ [isRTL ? 'left' : 'right']: 0 }}>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLang(lang.code);
                        setIsLangOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-white/10 transition-colors ${
                        currentLang === lang.code ? 'text-[#00FFD1]' : 'text-white/70'
                      }`}
                      style={{ textAlign: isRTL ? 'right' : 'left' }}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Button */}
            {hasItems() && (
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 text-[#00FFD1] hover:text-white transition-colors"
              >
                <ShoppingCart size={22} />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#00FFD1] text-black text-xs flex items-center justify-center">
                  !
                </span>
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? t('common.closeMenu') : t('common.openMenu')}
              className="2xl:hidden p-2 text-white"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="2xl:hidden bg-[#121212] border-t border-white/10 py-4">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() =>
                  item.path ? navigateToRoute(item.path) : scrollToSection(item.id)
                }
                className="block w-full text-left px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                style={{ textAlign: isRTL ? 'right' : 'left' }}
              >
                {item.label || t(`nav.${item.key}`)}
              </button>
            ))}
            <div className="px-4 pt-3">
              <button
                type="button"
                onClick={() => scrollToSection('contact')}
                className="btn-glass-primary w-full text-base"
              >
                {t('nav.projectCta')}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
